/**
 * A number input you can actually edit.
 *
 * ── The bug this exists to kill ─────────────────────────────────────────────
 * Every numeric input on the till was fully controlled and committed on every
 * keystroke:
 *
 *     value={value}
 *     onChange={(e) => onChange(Number(e.target.value) || 0)}
 *
 * There is no way to type a new number into that. To replace the "1" in a cart
 * line you must first clear it, and the instant the field goes empty
 * `Number('') || 0` commits a 0. On quantity that is destructive: the cart
 * store treats qty <= 0 as a removal, so backspacing to retype DELETED THE
 * LINE out from under the cashier. On price and discount it is merely
 * maddening — the field refuses to go empty, snapping back to 0 mid-edit.
 *
 * The second failure is subtler and bit the high-quantity confirm. Typing "20"
 * emits 2 and then 20, so the "2" was already committed by the time the dialog
 * asked about the 20. Cancelling left the line at 2 — a quantity the cashier
 * never chose and did not see, on a bill they were about to charge.
 *
 * ── The fix ────────────────────────────────────────────────────────────────
 * Hold what the user is typing as a STRING, separate from the committed
 * number. While that draft is present it is what shows, so a half-typed or
 * empty field is just a half-typed field — it commits nothing. Dropping the
 * draft re-reads the real value, which makes "revert" and "the owner changed
 * it underneath me" the same code path.
 *
 * An edit that never becomes a valid number in range is abandoned on blur, not
 * coerced. Reverting to the last good value is always safe; guessing is not.
 */
import { useState } from 'react';
import { cn } from '@/utils/cn';

export interface NumberDraftOptions {
  /** The committed value. Shown whenever the user is not mid-edit. */
  value: number;
  /** Called only with a valid, in-range number. Never with 0 from an empty field. */
  onCommit: (n: number) => void;
  /**
   * `change` keeps running totals live as you type — right for money, where
   * the cashier is watching the bill total react.
   *
   * `blur` waits for a finished edit — required wherever a partial number has
   * consequences. Quantity uses it so "20" is one decision and not a 2 that
   * already happened.
   */
  commitOn?: 'change' | 'blur';
  /** Below this, an edit is abandoned rather than committed. Defaults to 0. */
  min?: number;
  max?: number;
  /** Allow a decimal point. Off for counts, on for money. */
  decimals?: boolean;
  /** Render 0 as an empty field, so a placeholder can show through. */
  blankZero?: boolean;
}

/**
 * The draft-editing behaviour on its own, as props to spread onto an input.
 *
 * A hook rather than only a component because the till has two kinds of
 * numeric field — the bare input inside the quantity stepper and the styled
 * `Input` used for money — and they must not drift apart in behaviour just
 * because they differ in chrome.
 */
export function useNumberDraft({
  value,
  onCommit,
  commitOn = 'change',
  min = 0,
  max,
  decimals = false,
  blankZero = false,
}: NumberDraftOptions) {
  // null means "not editing" — show the committed value.
  const [draft, setDraft] = useState<string | null>(null);

  /** The draft as a number, or null when it is not one we may commit. */
  const parse = (raw: string): number | null => {
    const text = raw.trim();
    if (text === '') return null;
    const n = Number(text);
    if (!Number.isFinite(n)) return null;
    if (!decimals && !Number.isInteger(n)) return null;
    if (n < min) return null;
    if (max != null && n > max) return null;
    return n;
  };

  const commitDraft = () => {
    const n = draft === null ? null : parse(draft);
    // Drop the draft either way: on success the committed value now matches it,
    // on failure this is the revert.
    setDraft(null);
    if (n !== null && n !== value) onCommit(n);
  };

  const committedText = blankZero && value === 0 ? '' : String(value);

  return {
    // `text` with a numeric keypad, not `type="number"`. A number input reports
    // "" for anything it considers invalid mid-typing, so the draft would lose
    // characters the user can still see on screen — and it brings spinners that
    // every call site then has to style away.
    type: 'text' as const,
    inputMode: (decimals ? 'decimal' : 'numeric') as 'decimal' | 'numeric',
    value: draft ?? committedText,
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
      // Select all, so the common case — replace this number — is one keystroke
      // and never needs the backspace that used to delete the line.
      e.currentTarget.select();
    },
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      // Refuse a stray character outright rather than letting it into the draft
      // to fail parsing later.
      if (!(decimals ? /^\d*\.?\d*$/ : /^\d*$/).test(raw)) return;
      setDraft(raw);
      if (commitOn === 'change') {
        const n = parse(raw);
        if (n !== null && n !== value) onCommit(n);
      }
    },
    onBlur: commitDraft,
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitDraft();
        e.currentTarget.blur();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setDraft(null);
        e.currentTarget.blur();
      }
    },
  };
}

export type NumberFieldProps = NumberDraftOptions & {
  className?: string;
  id?: string;
  'aria-label'?: string;
  'data-testid'?: string;
  disabled?: boolean;
  placeholder?: string;
};

/** A bare input wired to {@link useNumberDraft}. */
export function NumberField({
  value,
  onCommit,
  commitOn,
  min,
  max,
  decimals,
  blankZero,
  className,
  ...rest
}: NumberFieldProps) {
  const props = useNumberDraft({ value, onCommit, commitOn, min, max, decimals, blankZero });
  return <input {...props} className={cn('tabular-nums', className)} {...rest} />;
}
